import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import {
  getEmptyLocations,
  getCycleCountCoverage,
  createCountFromOverdue,
  getCycleCountSchedule,
  updateCycleCountSchedule,
  runScheduleNow,
  testScheduleNotification,
  getUsers,
} from '../lib/api';
import {
  MapPin,
  Package,
  ArrowClockwise,
  Warning,
  CheckCircle,
  Clock,
  Download,
  Funnel,
  ListChecks,
  Calendar,
  Play,
  Lightning,
  Envelope,
  SlackLogo,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function downloadCSV(filename, rows, columns) {
  if (!rows || rows.length === 0) {
    toast.error('Nothing to export');
    return;
  }
  const head = columns.map((c) => c.label).join(',');
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = c.value(r);
          if (v === null || v === undefined) return '';
          const s = String(v).replace(/"/g, '""');
          return /[,\n"]/.test(s) ? `"${s}"` : s;
        })
        .join(',')
    )
    .join('\n');
  const blob = new Blob([head + '\n' + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({ label, value, sub, accent = 'slate' }) {
  const colors = {
    slate: 'text-foreground',
    blue: 'text-brand',
    red: 'text-red-700',
    amber: 'text-amber-700',
    green: 'text-emerald-700',
  };
  return (
    <div className="border border-border bg-card p-4">
      <p className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">{label}</p>
      <p className={`text-3xl font-bold font-mono mt-1 ${colors[accent] || colors.slate}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function EmptyLocationsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zone, setZone] = useState('');
  const [locationType, setLocationType] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (zone) params.zone = zone;
      if (locationType) params.location_type = locationType;
      const { data } = await getEmptyLocations(params);
      setData(data);
    } catch (e) {
      toast.error('Failed to load empty locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const columns = useMemo(
    () => [
      { label: 'Location Code', value: (r) => r.location_code },
      { label: 'Zone', value: (r) => r.zone },
      { label: 'Aisle', value: (r) => r.aisle },
      { label: 'Bay', value: (r) => r.bay },
      { label: 'Level', value: (r) => r.level },
      { label: 'Bin', value: (r) => r.bin },
      { label: 'Type', value: (r) => r.location_type },
      { label: 'Capacity', value: (r) => r.capacity },
      { label: 'Barcode', value: (r) => r.barcode },
      { label: 'Last Movement', value: (r) => r.last_movement_at },
      { label: 'Days Idle', value: (r) => r.days_idle },
    ],
    []
  );

  const exportCsv = () => {
    downloadCSV(`empty-locations-${new Date().toISOString().slice(0, 10)}.csv`, data?.locations || [], columns);
  };

  return (
    <div className="space-y-5" data-testid="empty-locations-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Active Locations" value={data?.total_active ?? '—'} accent="slate" />
        <StatCard label="Empty Right Now" value={data?.empty_count ?? '—'} accent="amber" sub="Available for putaway" />
        <StatCard label="Utilization" value={data ? `${data.utilization_pct}%` : '—'} accent="blue" sub="Filled vs total" />
      </div>

      <div className="flex items-end flex-wrap gap-3 border border-border bg-muted p-4">
        <div className="flex-1 min-w-[120px]">
          <Label className="text-xs font-bold uppercase tracking-wider">Zone</Label>
          <Input
            value={zone}
            onChange={(e) => setZone(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="e.g. A"
            data-testid="empty-loc-zone-filter"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-bold uppercase tracking-wider">Type</Label>
          <Input
            value={locationType}
            onChange={(e) => setLocationType(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="standard"
            data-testid="empty-loc-type-filter"
          />
        </div>
        <Button onClick={load} data-testid="empty-loc-apply-btn" className="bg-brand hover:bg-[#001F8A]">
          <Funnel size={14} className="mr-1" /> Apply
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="empty-loc-export-btn">
          <Download size={14} className="mr-1" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : !data?.locations?.length ? (
        <div className="border-2 border-dashed border-border p-12 text-center text-muted-foreground">
          <CheckCircle size={32} className="mx-auto mb-2 text-emerald-600" />
          No empty locations match the filters — your warehouse is humming!
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border">
              <tr className="text-left text-[10px] font-bold tracking-wider uppercase text-muted-foreground">
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Zone</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Capacity</th>
                <th className="px-3 py-2">Last Movement</th>
                <th className="px-3 py-2 text-right">Days Idle</th>
              </tr>
            </thead>
            <tbody>
              {data.locations.map((r) => (
                <tr key={r.id} className="border-b border-border hover:bg-muted" data-testid={`empty-loc-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono font-bold">{r.location_code}</td>
                  <td className="px-3 py-2">{r.zone}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.location_type}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.capacity}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.last_movement_at ? new Date(r.last_movement_at).toLocaleDateString() : (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-bold tracking-wider uppercase">
                        <Warning size={12} /> Never Used
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.days_idle == null ? '—' : r.days_idle > 30 ? (
                      <span className="text-red-600 font-bold">{r.days_idle}d</span>
                    ) : (
                      <span>{r.days_idle}d</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CycleCountCoveragePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overdueDays, setOverdueDays] = useState(90);
  const [dueSoonDays, setDueSoonDays] = useState(60);
  const [zone, setZone] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [createOpen, setCreateOpen] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [creating, setCreating] = useState(false);
  const [includeDueSoon, setIncludeDueSoon] = useState(false);
  const [createLimit, setCreateLimit] = useState(500);
  const [pickerId, setPickerId] = useState('');
  const [pickers, setPickers] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleRunning, setScheduleRunning] = useState(false);

  // Load pickers once
  useEffect(() => {
    getUsers({ role: 'picker' })
      .then(({ data }) => setPickers(data))
      .catch(() => setPickers([]));
  }, []);

  // Load schedule config once
  const loadSchedule = async () => {
    try {
      const { data } = await getCycleCountSchedule();
      setSchedule(data);
    } catch (e) {
      setSchedule(null);
    }
  };
  useEffect(() => { loadSchedule(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { overdue_days: overdueDays, due_soon_days: dueSoonDays };
      if (zone) params.zone = zone;
      const { data } = await getCycleCountCoverage(params);
      setData(data);
    } catch (e) {
      toast.error('Failed to load cycle count report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filteredRows = useMemo(() => {
    if (!data?.locations) return [];
    if (statusFilter === 'ALL') return data.locations;
    return data.locations.filter((r) => r.count_status === statusFilter);
  }, [data, statusFilter]);

  const columns = useMemo(
    () => [
      { label: 'Location Code', value: (r) => r.location_code },
      { label: 'Zone', value: (r) => r.zone },
      { label: 'Type', value: (r) => r.location_type },
      { label: 'Expected Qty', value: (r) => r.expected_qty },
      { label: 'Last Counted', value: (r) => r.last_counted_at || '' },
      { label: 'Days Since Count', value: (r) => r.days_since_count ?? '' },
      { label: 'Last Variance', value: (r) => r.last_variance ?? '' },
      { label: 'Status', value: (r) => r.count_status },
    ],
    []
  );

  const exportCsv = () => {
    downloadCSV(`cycle-count-coverage-${new Date().toISOString().slice(0, 10)}.csv`, filteredRows, columns);
  };

  const includedStatuses = useMemo(() => {
    const s = ['NEVER', 'OVERDUE'];
    if (includeDueSoon) s.push('DUE_SOON');
    return s;
  }, [includeDueSoon]);

  const previewCount = useMemo(() => {
    if (!data?.locations) return 0;
    return data.locations.filter((r) => includedStatuses.includes(r.count_status)).length;
  }, [data, includedStatuses]);

  const handleCreateCount = async () => {
    setCreating(true);
    try {
      const payload = {
        overdue_days: overdueDays,
        due_soon_days: dueSoonDays,
        include_statuses: includedStatuses,
        limit: createLimit,
      };
      if (zone) payload.zone = zone;
      if (pickerId) payload.picker_id = pickerId;
      const { data: res } = await createCountFromOverdue(payload);
      setCreateResult(res);
      toast.success(`Cycle count ${res.count.count_number} created — ${res.targeted_count} locations queued`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create cycle count');
    } finally {
      setCreating(false);
    }
  };

  const saveSchedule = async (patch) => {
    setScheduleSaving(true);
    try {
      const { data } = await updateCycleCountSchedule(patch);
      setSchedule(data);
      toast.success('Schedule updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const runNow = async () => {
    setScheduleRunning(true);
    try {
      const { data } = await runScheduleNow();
      if (data.created) {
        toast.success(`Scheduler fired — ${data.count_number} created for ${data.targeted} locations`);
      } else {
        toast(`No matching locations (${data.reason}). Nothing to create.`);
      }
      loadSchedule();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setScheduleRunning(false);
    }
  };

  const sendTestPing = async () => {
    try {
      const { data } = await testScheduleNotification();
      toast.success(`Test ping sent — ${data.emails_sent} email(s)${data.slack_sent ? ' + Slack' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test notification failed');
    }
  };

  const statusStyles = {
    NEVER: 'bg-red-100 text-red-800',
    OVERDUE: 'bg-amber-100 text-amber-900',
    DUE_SOON: 'bg-yellow-50 text-yellow-800',
    OK: 'bg-emerald-100 text-emerald-800',
  };

  return (
    <div className="space-y-5" data-testid="cycle-count-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Active Locations" value={data?.total_active ?? '—'} accent="slate" />
        <StatCard label="Never Counted" value={data?.never_counted ?? '—'} accent="red" />
        <StatCard label={`Overdue (≥ ${data?.overdue_days ?? overdueDays}d)`} value={data?.overdue ?? '—'} accent="amber" />
        <StatCard label={`Due Soon (${data?.due_soon_days ?? dueSoonDays}–${data?.overdue_days ?? overdueDays}d)`} value={data?.due_soon ?? '—'} accent="amber" />
        <StatCard label="Counted last 30d" value={data?.counted_last_30d ?? '—'} accent="green" />
      </div>

      <div className="flex items-end flex-wrap gap-3 border border-border bg-muted p-4">
        <div className="flex-1 min-w-[100px]">
          <Label className="text-xs font-bold uppercase tracking-wider">Zone</Label>
          <Input
            value={zone}
            onChange={(e) => setZone(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="e.g. A"
            data-testid="cc-zone-filter"
          />
        </div>
        <div className="w-32">
          <Label className="text-xs font-bold uppercase tracking-wider">Overdue (days)</Label>
          <Input
            type="number"
            min={1}
            value={overdueDays}
            onChange={(e) => setOverdueDays(Number(e.target.value || 90))}
            data-testid="cc-overdue-days"
          />
        </div>
        <div className="w-32">
          <Label className="text-xs font-bold uppercase tracking-wider">Due Soon (days)</Label>
          <Input
            type="number"
            min={1}
            value={dueSoonDays}
            onChange={(e) => setDueSoonDays(Number(e.target.value || 60))}
            data-testid="cc-due-soon-days"
          />
        </div>
        <Button onClick={load} data-testid="cc-apply-btn" className="bg-brand hover:bg-[#001F8A]">
          <ArrowClockwise size={14} className="mr-1" /> Refresh
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="cc-export-btn">
          <Download size={14} className="mr-1" /> Export CSV
        </Button>
      </div>

      {/* ==== Weekly Auto-Schedule card ==== */}
      {schedule && (
        <div className="border-2 border-brand/15 bg-card p-4" data-testid="cc-schedule-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 bg-brand flex items-center justify-center shrink-0">
                <Calendar size={18} weight="fill" className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-base tracking-tight">Weekly Auto-Schedule</span>
                  {schedule.enabled ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold tracking-wider uppercase">
                      <Lightning size={10} weight="fill" /> Active
                    </span>
                  ) : (
                    <span className="inline-block px-1.5 py-0.5 bg-slate-200 text-foreground text-[10px] font-bold tracking-wider uppercase">
                      Paused
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {schedule.enabled ? (
                    <>Auto-creates a cycle count every <b>{DAYS_OF_WEEK[schedule.day_of_week]}</b> at <b>{String(schedule.hour_utc).padStart(2, '0')}:00 UTC</b> for {schedule.include_statuses.join(' + ')} locations.</>
                  ) : (
                    <>Enable to spawn a fresh overdue-count every week automatically.</>
                  )}
                </p>
                {(schedule.notify_emails?.length > 0 || schedule.notify_slack_webhook) && (
                  <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                    {schedule.notify_emails?.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Envelope size={11} className="text-brand" />
                        {schedule.notify_emails.length} recipient{schedule.notify_emails.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {schedule.notify_slack_webhook && (
                      <span className="inline-flex items-center gap-1">
                        <SlackLogo size={11} className="text-brand" />
                        Slack configured
                      </span>
                    )}
                  </p>
                )}
                {schedule.last_run_at && (
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                    Last run: {new Date(schedule.last_run_at).toLocaleString()}{' '}
                    {schedule.last_run_count_number ? (
                      <>· <b className="text-brand">{schedule.last_run_count_number}</b> · {schedule.last_run_targeted} locations</>
                    ) : (
                      <>· no matches</>
                    )}
                  </p>
                )}
                {schedule.last_run_error && (
                  <p className="text-[11px] text-red-600 mt-1">Last error: {schedule.last_run_error}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={runNow}
                disabled={scheduleRunning}
                data-testid="cc-schedule-run-now-btn"
              >
                <Play size={12} className="mr-1" /> {scheduleRunning ? 'Running…' : 'Run now'}
              </Button>
              <Button
                onClick={() => setScheduleOpen(true)}
                size="sm"
                data-testid="cc-schedule-configure-btn"
                className="bg-brand hover:bg-[#001F8A]"
              >
                Configure
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Filter:</span>
        {['ALL', 'NEVER', 'OVERDUE', 'DUE_SOON', 'OK'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`cc-filter-${s}`}
            className={`px-2.5 py-1 text-[11px] font-bold tracking-wider uppercase border transition-colors ${
              statusFilter === s
                ? 'bg-brand text-white border-brand'
                : 'bg-card text-muted-foreground border-border hover:border-brand'
            }`}
          >
            {s.replace('_', ' ')}
          </button>
        ))}
        <div className="flex-1" />
        <Button
          onClick={() => { setCreateResult(null); setCreateOpen(true); }}
          disabled={!data || previewCount === 0}
          data-testid="create-count-from-overdue-btn"
          className="bg-brand hover:bg-[#001F8A] disabled:opacity-50"
        >
          <ListChecks size={14} className="mr-1" />
          Create Count from Overdue
          {data && previewCount > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-card text-brand text-[10px] font-mono font-bold">
              {previewCount}
            </span>
          )}
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : !filteredRows.length ? (
        <div className="border-2 border-dashed border-border p-12 text-center text-muted-foreground">
          No locations match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border">
              <tr className="text-left text-[10px] font-bold tracking-wider uppercase text-muted-foreground">
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Zone</th>
                <th className="px-3 py-2 text-right">Expected</th>
                <th className="px-3 py-2">Last Counted</th>
                <th className="px-3 py-2 text-right">Days Since</th>
                <th className="px-3 py-2 text-right">Last Variance</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} className="border-b border-border hover:bg-muted" data-testid={`cc-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono font-bold">{r.location_code}</td>
                  <td className="px-3 py-2">{r.zone}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.expected_qty}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.last_counted_at ? new Date(r.last_counted_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.days_since_count == null ? '—' : `${r.days_since_count}d`}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.last_variance == null ? '—' : r.last_variance !== 0 ? (
                      <span className="text-red-600 font-bold">{r.last_variance > 0 ? '+' : ''}{r.last_variance}</span>
                    ) : (
                      <span className="text-emerald-600">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${statusStyles[r.count_status]}`}>
                      {r.count_status.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-count-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <ListChecks size={20} className="text-brand" weight="fill" />
              Create Cycle Count
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Queue a pending cycle count covering every location flagged by this report.
              Pickers will see it in the Cycle Count module.
            </DialogDescription>
          </DialogHeader>

          {!createResult ? (
            <div className="space-y-4">
              <div className="border border-border bg-muted p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Zone filter</span>
                  <span className="font-mono font-bold">{zone || 'All zones'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Overdue threshold</span>
                  <span className="font-mono">{overdueDays} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Due-soon threshold</span>
                  <span className="font-mono">{dueSoonDays} days</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="text-muted-foreground">Statuses</span>
                  <span className="font-mono font-bold">{includedStatuses.join(' + ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Locations to count</span>
                  <span className="text-xl font-bold font-mono text-brand">{previewCount}</span>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeDueSoon}
                  onChange={(e) => setIncludeDueSoon(e.target.checked)}
                  data-testid="create-count-include-due-soon"
                />
                <span className="text-sm">Also include <b>Due Soon</b> locations</span>
              </label>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">
                  Assign to picker <span className="text-muted-foreground font-normal normal-case">(optional)</span>
                </Label>
                <Select value={pickerId || 'unassigned'} onValueChange={(v) => setPickerId(v === 'unassigned' ? '' : v)}>
                  <SelectTrigger data-testid="create-count-picker-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">— Unassigned (any picker can claim) —</SelectItem>
                    {pickers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} · <span className="text-xs text-muted-foreground">{p.email}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">
                  Max locations (safety cap)
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={2000}
                  value={createLimit}
                  onChange={(e) => setCreateLimit(Number(e.target.value || 500))}
                  data-testid="create-count-limit"
                />
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                  data-testid="create-count-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCount}
                  disabled={creating || previewCount === 0}
                  data-testid="create-count-confirm-btn"
                  className="bg-brand hover:bg-[#001F8A]"
                >
                  {creating ? 'Creating…' : `Create count for ${previewCount} locations`}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="create-count-success">
              <div className="border-2 border-emerald-500 bg-emerald-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={18} weight="fill" className="text-emerald-700" />
                  <span className="text-xs font-bold tracking-wider uppercase text-emerald-900">
                    Count Created
                  </span>
                </div>
                <p className="text-sm text-foreground">
                  Count <span className="font-mono font-bold">{createResult.count.count_number}</span>{' '}
                  has been queued with <b>{createResult.targeted_count}</b> locations.
                </p>
              </div>
              <div className="max-h-52 overflow-y-auto border border-border divide-y divide-slate-100 text-xs font-mono">
                {createResult.targeted_locations.map((l) => (
                  <div key={l.id} className="flex justify-between px-3 py-1.5 hover:bg-muted">
                    <span className="font-bold">{l.location_code}</span>
                    <span className={`${statusStyles[l.count_status]} px-1.5 text-[10px] font-bold uppercase tracking-wider`}>
                      {l.count_status.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                  data-testid="create-count-done-btn"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==== Schedule config dialog ==== */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-lg" data-testid="cc-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Calendar size={20} className="text-brand" weight="fill" />
              Weekly Auto-Schedule
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Automatically queue a cycle count every week. The scheduler checks hourly and fires once
              per slot; all times are UTC.
            </DialogDescription>
          </DialogHeader>

          {schedule && (
            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer border border-border p-3 bg-muted">
                <input
                  type="checkbox"
                  checked={!!schedule.enabled}
                  onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
                  data-testid="cc-schedule-enabled"
                />
                <span className="text-sm font-bold">Enable weekly auto-schedule</span>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Day (UTC)</Label>
                  <Select
                    value={String(schedule.day_of_week)}
                    onValueChange={(v) => setSchedule({ ...schedule, day_of_week: Number(v) })}
                  >
                    <SelectTrigger data-testid="cc-schedule-day">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Hour (0–23 UTC)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={schedule.hour_utc}
                    onChange={(e) => setSchedule({ ...schedule, hour_utc: Math.max(0, Math.min(23, Number(e.target.value || 0))) })}
                    data-testid="cc-schedule-hour"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Include statuses</Label>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {['NEVER', 'OVERDUE', 'DUE_SOON'].map((s) => {
                    const on = schedule.include_statuses?.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          const cur = schedule.include_statuses || [];
                          const next = on ? cur.filter((x) => x !== s) : [...cur, s];
                          setSchedule({ ...schedule, include_statuses: next });
                        }}
                        data-testid={`cc-schedule-status-${s}`}
                        className={`px-2 py-1 text-[11px] font-bold tracking-wider uppercase border ${
                          on ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'
                        }`}
                      >
                        {s.replace('_', ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Zone filter</Label>
                  <Input
                    value={schedule.zone || ''}
                    onChange={(e) => setSchedule({ ...schedule, zone: e.target.value.toUpperCase() || null })}
                    placeholder="All zones"
                    data-testid="cc-schedule-zone"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Max locations</Label>
                  <Input
                    type="number"
                    min={1}
                    max={2000}
                    value={schedule.limit}
                    onChange={(e) => setSchedule({ ...schedule, limit: Math.max(1, Math.min(2000, Number(e.target.value || 500))) })}
                    data-testid="cc-schedule-limit"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">
                  Assign to picker <span className="text-muted-foreground font-normal normal-case">(optional)</span>
                </Label>
                <Select
                  value={schedule.picker_id || 'unassigned'}
                  onValueChange={(v) => setSchedule({ ...schedule, picker_id: v === 'unassigned' ? null : v })}
                >
                  <SelectTrigger data-testid="cc-schedule-picker">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">— Unassigned (any picker) —</SelectItem>
                    {pickers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ==== Notifications section ==== */}
              <div className="border border-border bg-muted p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold tracking-wider uppercase text-foreground">
                    Notifications <span className="text-muted-foreground font-normal normal-case">(mocked)</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={sendTestPing}
                    data-testid="cc-schedule-test-ping-btn"
                    className="text-xs"
                  >
                    <PaperPlaneTilt size={12} className="mr-1" /> Send test ping
                  </Button>
                </div>

                <div>
                  <Label className="text-[11px] flex items-center gap-1">
                    <Envelope size={12} /> Email recipients <span className="text-muted-foreground normal-case">(comma-separated)</span>
                  </Label>
                  <Input
                    value={(schedule.notify_emails || []).join(', ')}
                    onChange={(e) =>
                      setSchedule({
                        ...schedule,
                        notify_emails: e.target.value
                          .split(',')
                          .map((x) => x.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="ops@stockaris.com, warehouse-lead@stockaris.com"
                    data-testid="cc-schedule-emails"
                  />
                </div>

                <div>
                  <Label className="text-[11px] flex items-center gap-1">
                    <SlackLogo size={12} /> Slack webhook URL <span className="text-muted-foreground normal-case">(optional)</span>
                  </Label>
                  <Input
                    value={schedule.notify_slack_webhook || ''}
                    onChange={(e) =>
                      setSchedule({ ...schedule, notify_slack_webhook: e.target.value || null })
                    }
                    placeholder="https://hooks.slack.com/services/…"
                    data-testid="cc-schedule-slack-webhook"
                    className="font-mono text-xs"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={!!schedule.notify_on_create}
                    onChange={(e) => setSchedule({ ...schedule, notify_on_create: e.target.checked })}
                    data-testid="cc-schedule-notify-on-create"
                  />
                  <span>Also notify on <b>manual</b> &ldquo;Create from Overdue&rdquo; actions</span>
                </label>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setScheduleOpen(false)} data-testid="cc-schedule-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    await saveSchedule({
                      enabled: schedule.enabled,
                      day_of_week: schedule.day_of_week,
                      hour_utc: schedule.hour_utc,
                      include_statuses: schedule.include_statuses,
                      zone: schedule.zone,
                      limit: schedule.limit,
                      picker_id: schedule.picker_id,
                      notify_emails: schedule.notify_emails || [],
                      notify_slack_webhook: schedule.notify_slack_webhook,
                      notify_on_create: schedule.notify_on_create,
                    });
                    setScheduleOpen(false);
                  }}
                  disabled={scheduleSaving}
                  data-testid="cc-schedule-save-btn"
                  className="bg-brand hover:bg-[#001F8A]"
                >
                  {scheduleSaving ? 'Saving…' : 'Save schedule'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function LocationReports() {
  return (
    <Layout>
      <div className="px-6 py-8 max-w-7xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={18} weight="fill" className="text-brand" />
            <span className="text-[10px] font-bold tracking-[0.2em] text-brand uppercase">
              Location Reports
            </span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Location Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Empty bins for putaway planning and cycle count coverage across every location.
          </p>
        </div>

        <Tabs defaultValue="empty">
          <TabsList>
            <TabsTrigger value="empty" data-testid="tab-empty-locations">
              <Package size={14} className="mr-1" /> Empty Locations
            </TabsTrigger>
            <TabsTrigger value="cycle" data-testid="tab-cycle-count">
              <Clock size={14} className="mr-1" /> Cycle Count Coverage
            </TabsTrigger>
          </TabsList>
          <TabsContent value="empty" className="mt-5">
            <EmptyLocationsPanel />
          </TabsContent>
          <TabsContent value="cycle" className="mt-5">
            <CycleCountCoveragePanel />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
